// ========== CURRENCY FORMATTER UTILITY ==========
// Format prices according to facility's selected currency
import { database, ref, get, onValue } from '../pages/Auth/db/firebase';

const DEFAULT_CURRENCY_FORMATS = {
  USD: { symbol: '$', position: 'before', decimals: 2 },
  EUR: { symbol: '€', position: 'before', decimals: 2 },
  RWF: { symbol: 'Rwf', position: 'after', decimals: 0 }
};

// Cache for facility currencies and custom currencies
const currencyCache = {};
const customCurrencyCache = {};

// Get currency for a facility
export async function getFacilityCurrency(facilityId) {
  if (currencyCache[facilityId]) {
    return currencyCache[facilityId];
  }

  try {
    const settingsRef = ref(database, `facilities/${facilityId}/settings`);
    const snapshot = await get(settingsRef);
    
    if (snapshot.exists()) {
      const settings = snapshot.val();
      const currency = settings.currency || 'USD';
      currencyCache[facilityId] = currency;
      return currency;
    }
  } catch (error) {
    console.error('Error fetching currency:', error);
  }

  return 'USD'; // Default
}

// Listen to currency changes
export function listenToCurrency(facilityId, callback) {
  const settingsRef = ref(database, `facilities/${facilityId}/settings`);
  
  const handler = (snapshot) => {
    if (snapshot.exists()) {
      const settings = snapshot.val();
      const currency = settings.currency || 'USD';
      currencyCache[facilityId] = currency;
      callback(currency);
    }
  };
  
  onValue(settingsRef, handler);
  return () => onValue(settingsRef, handler);
}

// Get custom currencies for a facility
export async function getCustomCurrencies(facilityId) {
  if (customCurrencyCache[facilityId]) {
    return customCurrencyCache[facilityId];
  }

  try {
    const customCurrenciesRef = ref(database, `facilities/${facilityId}/customCurrencies`);
    const snapshot = await get(customCurrenciesRef);
    
    if (snapshot.exists()) {
      const currencies = snapshot.val();
      const currencyMap = {};
      Object.values(currencies).forEach(curr => {
        currencyMap[curr.code] = curr;
      });
      customCurrencyCache[facilityId] = currencyMap;
      return currencyMap;
    }
  } catch (error) {
    console.error('Error fetching custom currencies:', error);
  }

  customCurrencyCache[facilityId] = {};
  return {};
}

// Get currency format (checks custom currencies first, then defaults)
export async function getCurrencyFormat(currency, facilityId = null) {
  // First check custom currencies if facilityId provided
  if (facilityId) {
    const customCurrencies = await getCustomCurrencies(facilityId);
    if (customCurrencies[currency]) {
      return customCurrencies[currency];
    }
  }
  
  // Fall back to default currencies
  return DEFAULT_CURRENCY_FORMATS[currency] || DEFAULT_CURRENCY_FORMATS.USD;
}

// Format price with currency (synchronous - checks cache for custom currencies)
export function formatPrice(amount, currency = 'USD', facilityId = null) {
  // Check custom currencies cache first if facilityId provided
  let format = DEFAULT_CURRENCY_FORMATS[currency];
  
  if (facilityId && customCurrencyCache[facilityId] && customCurrencyCache[facilityId][currency]) {
    format = customCurrencyCache[facilityId][currency];
  } else if (!format) {
    format = DEFAULT_CURRENCY_FORMATS.USD;
  }
  
  const value = format.decimals === 0 
    ? Math.round(amount).toLocaleString()
    : amount.toFixed(format.decimals);

  if (format.position === 'before') {
    return `${format.symbol}${value}`;
  } else {
    return `${value} ${format.symbol}`;
  }
}

// Format price with currency (async version that checks custom currencies)
export async function formatPriceAsync(amount, currency = 'USD', facilityId = null) {
  const format = await getCurrencyFormat(currency, facilityId);
  const value = format.decimals === 0 
    ? Math.round(amount).toLocaleString()
    : amount.toFixed(format.decimals);

  if (format.position === 'before') {
    return `${format.symbol}${value}`;
  } else {
    return `${value} ${format.symbol}`;
  }
}

// Get currency symbol
export async function getCurrencySymbol(currency = 'USD', facilityId = null) {
  if (facilityId) {
    const format = await getCurrencyFormat(currency, facilityId);
    return format.symbol || '$';
  }
  return DEFAULT_CURRENCY_FORMATS[currency]?.symbol || '$';
}

// Format price with facility's currency (async version)
export async function formatPriceWithFacility(amount, facilityId) {
  const currency = await getFacilityCurrency(facilityId);
  return formatPrice(amount, currency);
}

// Format price with known currency (synchronous version - uses cached custom currencies)
export function formatPriceWithCurrency(amount, currency, facilityId = null) {
  return formatPrice(amount, currency, facilityId);
}

// Pre-load custom currencies for a facility (call this when component mounts)
export async function preloadCustomCurrencies(facilityId) {
  if (!facilityId) return;
  await getCustomCurrencies(facilityId); // This will populate the cache
}

// Clear currency cache (useful when custom currencies are updated)
export function clearCurrencyCache(facilityId) {
  if (facilityId) {
    delete currencyCache[facilityId];
    delete customCurrencyCache[facilityId];
  } else {
    Object.keys(currencyCache).forEach(key => delete currencyCache[key]);
    Object.keys(customCurrencyCache).forEach(key => delete customCurrencyCache[key]);
  }
}
// ========== CURRENCY FORMATTER UTILITY ENDS ==========
